import { useMemo } from 'react';
import { Box, Stack, Typography } from '@common-components';
import { StateCode } from 'enums';
import { messages } from 'i18n';
import { Suggestion } from 'types';
import { ConfidenceScore } from 'components/hookFormComponents/ConfidenceScore';
import FormAutocomplete from 'components/hookFormComponents/FormAutocomplete/FormAutocomplete';
import FormTextField from 'components/hookFormComponents/FormTextField';
import { AIReason } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/AIReason';

const { fields } = messages.carrierSubmissionWorkspace;

const stateOptions = Object.values(StateCode).map((state) => ({
  label: state,
  value: state,
}));

interface CarrierSubmissionAddressFieldsProps {
  suggestion?: Suggestion;
}

export default function CarrierSubmissionAddressFields({ suggestion }: CarrierSubmissionAddressFieldsProps) {
  const addressValue = suggestion?.value as Record<string, string> | undefined;
  const reason = useMemo(() => (suggestion ? <AIReason suggestion={suggestion} /> : undefined), [suggestion]);

  const line1Suggestion = useMemo(
    () => (addressValue?.line1 ? { value: addressValue.line1, reason } : undefined),
    [addressValue?.line1, reason],
  );
  const line2Suggestion = useMemo(
    () => (addressValue?.line2 ? { value: addressValue.line2, reason } : undefined),
    [addressValue?.line2, reason],
  );
  const citySuggestion = useMemo(
    () => (addressValue?.city ? { value: addressValue.city, reason } : undefined),
    [addressValue?.city, reason],
  );
  const stateSuggestion = useMemo(
    () => (addressValue?.state ? { value: addressValue.state, reason } : undefined),
    [addressValue?.state, reason],
  );
  const postalCodeSuggestion = useMemo(
    () => (addressValue?.postalCode ? { value: addressValue.postalCode, reason } : undefined),
    [addressValue?.postalCode, reason],
  );

  return (
    <>
      <Box display="flex" alignItems="baseline" justifyContent="space-between" mt={3} mb={1}>
        <Typography variant="body2Bold" textTransform="capitalize">
          {fields.mailingAddress}
        </Typography>
        <ConfidenceScore confidence={suggestion?.confidence} />
      </Box>
      <Stack sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
        <FormTextField
          name="insuredMailingAddress.line1"
          id="insuredMailingAddress.line1"
          label={fields.addressLine1}
          suggestion={line1Suggestion}
          enhancedRequired
        />
        <FormTextField
          name="insuredMailingAddress.line2"
          id="insuredMailingAddress.line2"
          label={fields.addressLine2}
          suggestion={line2Suggestion}
          optional
        />
        <FormTextField
          name="insuredMailingAddress.city"
          id="insuredMailingAddress.city"
          label={fields.city}
          suggestion={citySuggestion}
          enhancedRequired
        />
        <Stack direction="row" gap={3}>
          <FormAutocomplete
            name="insuredMailingAddress.state"
            label={fields.state}
            options={stateOptions}
            suggestion={stateSuggestion}
            enhancedRequired
          />
          <FormTextField
            name="insuredMailingAddress.postalCode"
            id="insuredMailingAddress.postalCode"
            label={fields.zipCode}
            suggestion={postalCodeSuggestion}
            isNumberFormat
            numberFormatProps={{
              prefix: '',
              thousandSeparator: false,
              decimalScale: 0,
              allowLeadingZeros: true,
            }}
            enhancedRequired
          />
        </Stack>
      </Stack>
    </>
  );
}
